import React, { useState } from "react";
import { Button, Container, Row, Col, Card } from "react-bootstrap";
import { motion } from "framer-motion";
import { FaAmbulance, FaCar, FaHome, FaArrowLeft } from "react-icons/fa";
import styled from "styled-components";
import Dispatch from "../VehicleType/Dispatch";
import AmbulanceFlow from "./AmbulanceFlow";
import HomeCareFlow from "./HomeCareFlow";

const StyledContainer = styled(Container)`
  min-height: 100vh;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
`;

const MainCard = styled(Card)`
  border: none;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  max-width: 1000px;
  width: 100%;
  overflow: hidden;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 0.75rem;
  }
  
  p {
    font-size: 1.125rem;
    color: #6c757d;
    margin: 0;
  }
`;

const ServiceCard = styled(Card)`
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  background: ${props => props.bgColor};
  border-color: ${props => props.borderColor};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    border-color: ${props => props.hoverBorderColor};
    background: ${props => props.hoverBgColor};
  }
`;

const IconWrapper = styled.div`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  color: ${props => props.color};
  transition: transform 0.3s ease;
  
  ${ServiceCard}:hover & {
    transform: scale(1.1);
  }
`;

const ServiceButton = styled(Button)`
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  font-size: 1rem;
  width: 100%;
  border: none;
  transition: all 0.3s ease;
  background: ${props => props.bgColor};
  
  &:hover {
    background: ${props => props.hoverColor} !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:focus {
    background: ${props => props.hoverColor} !important;
    box-shadow: 0 0 0 3px ${props => props.focusColor};
  }
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #6c757d;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 500;
  margin-bottom: 2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #5a6268 !important;
    transform: translateY(-1px);
  }
  
  &:focus {
    background: #5a6268 !important;
    box-shadow: 0 0 0 3px rgba(108, 117, 125, 0.25);
  }
`;

const HelpText = styled.p`
  text-align: center;
  color: #6c757d;
  font-size: 0.875rem;
  margin-top: 2rem;
  margin-bottom: 0;
`;


const AmbulanceOrRidePage = ({ socket }) => {
  const [mode, setMode] = useState(null);
  
  const goHome = () => setMode(null);

  const dispatchTypes = [
    {
      id: "emergency",
      title: "Emergency",
      subtitle: "Immediate medical assistance",
      icon: FaAmbulance,
      bgColor: "#fef2f2",
      borderColor: "#fecaca",
      hoverBgColor: "#fee2e2",
      hoverBorderColor: "#f87171",
      iconColor: "#dc2626",
      buttonBgColor: "#dc2626",
      buttonHoverColor: "#b91c1c",
      focusColor: "rgba(220, 38, 38, 0.25)"
    },
    {
      id: "non_emergency",
      title: "Non-Emergency",
      subtitle: "Medical transport services",
      icon: FaCar,
      bgColor: "#eff6ff",
      borderColor: "#bfdbfe",
      hoverBgColor: "#dbeafe",
      hoverBorderColor: "#60a5fa",
      iconColor: "#2563eb",
      buttonBgColor: "#2563eb",
      buttonHoverColor: "#1d4ed8",
      focusColor: "rgba(37, 99, 235, 0.25)"
    },
    {
      id: "home_care",
      title: "Home Care",
      subtitle: "In-home medical services",
      icon: FaHome,
      bgColor: "#f0fdf4",
      borderColor: "#bbf7d0",
      hoverBgColor: "#dcfce7",
      hoverBorderColor: "#4ade80",
      iconColor: "#16a34a",
      buttonBgColor: "#16a34a",
      buttonHoverColor: "#15803d",
      focusColor: "rgba(22, 163, 74, 0.25)"
    }
  ];

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 }
  };

  const pageTransition = {
    type: "tween",
    ease: "easeInOut",
    duration: 0.4
  };

  // When a mode is selected, render with minimal styling to not interfere with child components
  if (mode) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
        <div style={{ padding: '1rem' }}>
          <motion.div
            initial="initial"
            animate="in"
            exit="out"
            variants={pageVariants}
            transition={pageTransition}
          >
            <div style={{ marginBottom: '1rem' }}>
              <BackButton onClick={goHome}>
                <FaArrowLeft />
                Back to Selection
              </BackButton>
            </div>
            
            {/* Render child components without constraining containers */}
            {mode === "emergency" ? (
              <AmbulanceFlow socket={socket} />
            ) : mode === "home_care" ? (
              <HomeCareFlow socket={socket} />
            ) : (
              <Dispatch socket={socket} />
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  // Main selection interface with full styling
  return (
    <StyledContainer fluid>
      <motion.div
        initial="initial"
        animate="in"
        exit="out"
        variants={pageVariants}
        transition={pageTransition}
        style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
      >
        <MainCard>
          <Card.Body style={{ padding: '3rem' }}>
            <HeaderSection>
              <h1>Dispatch Center</h1>
              <p>Select the type of service you need</p>
            </HeaderSection>

            <Row className="g-4 justify-content-center">
              {dispatchTypes.map((type) => {
                const IconComponent = type.icon;
                return (
                  <Col md={4} key={type.id} className="d-flex">
                    <ServiceCard
                      as={motion.div}
                      whileHover={{ y: -4 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setMode(type.id)}
                      bgColor={type.bgColor}
                      borderColor={type.borderColor}
                      hoverBgColor={type.hoverBgColor}
                      hoverBorderColor={type.hoverBorderColor}
                      style={{ width: '100%' }}
                    >
                      <Card.Body style={{ padding: '2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <IconWrapper color={type.iconColor}>
                            <IconComponent />
                          </IconWrapper>
                          <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#2c3e50', marginBottom: '0.75rem' }}>
                            {type.title}
                          </h3>
                          <p style={{ color: '#6c757d', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                            {type.subtitle}
                          </p>
                        </div>
                        <ServiceButton
                          bgColor={type.buttonBgColor}
                          hoverColor={type.buttonHoverColor}
                          focusColor={type.focusColor}
                        >
                          Select Service
                        </ServiceButton>
                      </Card.Body>
                    </ServiceCard>
                  </Col>
                );
              })}
            </Row>

            <HelpText>
              Need help choosing? Contact our support team for assistance.
            </HelpText>
          </Card.Body>
        </MainCard>
      </motion.div>
    </StyledContainer>
  );
};

export default AmbulanceOrRidePage;
